import {
  ActionConfirmationModal,
  Button,
  LoadingSpinner,
} from "@/components/ui";
import { useTiptapMentions } from "@/hooks/useTiptapMentions";
import {
  useCreateObjectUpdate,
  useEditObjectUpdate,
} from "@/lib/data/react-query/updates/object-updates";
import type {
  ObjectUpdate,
  UpdateAnchorType,
} from "@/lib/data/schemas/updates/ObjectUpdateSchema";
import { SLIDEOVER_CONFIRM_CLOSE_DESCRIPTION } from "@/lib/data/static/slideoverStatics";
import type { MentionOption } from "@/lib/utils/mentionUtils";
import { EditorContent } from "@tiptap/react";
import { useEffect, useState } from "react";

export type UpdateComposerState = {
  isDirty: boolean;
  isSubmitting: boolean;
};

interface ObjectUpdateComposerProps {
  anchorObjectType: UpdateAnchorType;
  anchorObjectId: string;
  update?: ObjectUpdate;
  autoFocus?: boolean;
  disabled?: boolean;
  onSaved?: () => void;
  onCancel?: () => void;
  onStateChange?: (state: UpdateComposerState) => void;
}

const noMentionOptions: MentionOption[] = [];

export function ObjectUpdateComposer({
  anchorObjectType,
  anchorObjectId,
  update,
  autoFocus = false,
  disabled = false,
  onSaved,
  onCancel,
  onStateChange,
}: ObjectUpdateComposerProps) {
  const [initialMessage] = useState(update?.message ?? "");
  const [message, setMessage] = useState(initialMessage);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const createUpdate = useCreateObjectUpdate();
  const editUpdate = useEditObjectUpdate();
  const isSubmitting = createUpdate.isPending || editUpdate.isPending;
  const isDirty = message !== initialMessage;
  const { editor, getDisplayMessage, setContent, hasInitializedEditor } =
    useTiptapMentions({
      plainText: true,
      enableMentions: false,
      mentionOptions: noMentionOptions,
      isLoading: false,
      initialContent: initialMessage,
      isEditable: !disabled && !isSubmitting,
      onUpdate: setMessage,
      className:
        "max-h-[240px] min-h-[120px] overflow-y-auto border-0 bg-transparent px-4 py-3 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none",
      placeholder: update ? "Edit your update..." : "Add an update...",
      ariaLabel: update ? "Edit update message" : "Update message",
    });
  useEffect(() => {
    if (autoFocus && hasInitializedEditor && !disabled)
      editor?.commands.focus("end");
  }, [autoFocus, editor, hasInitializedEditor, disabled]);
  useEffect(() => {
    onStateChange?.({ isDirty, isSubmitting });
  }, [isDirty, isSubmitting, onStateChange]);
  useEffect(
    () => () => {
      onStateChange?.({ isDirty: false, isSubmitting: false });
    },
    [onStateChange],
  );

  const handleSubmit = () => {
    const submittedMessage = getDisplayMessage().trim();
    if (!submittedMessage || disabled || isSubmitting || !editor) return;
    const onSuccess = () => {
      if (!update) {
        setContent("");
        setMessage("");
      }
      onSaved?.();
    };
    if (update) {
      if (!update.canEdit) return;
      editUpdate.mutate(
        { id: update.id, message: submittedMessage },
        { onSuccess },
      );
    } else {
      createUpdate.mutate(
        { anchorObjectType, anchorObjectId, message: submittedMessage },
        { onSuccess },
      );
    }
  };

  return (
    <div className="border-border bg-card rounded-xl border shadow-sm">
      <EditorContent editor={editor} />
      <div className="flex justify-end gap-2 px-4 py-2.5">
        {onCancel ? (
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting}
            onClick={() => {
              if (isDirty) setConfirmCancel(true);
              else onCancel();
            }}
          >
            Cancel
          </Button>
        ) : null}
        <Button
          type="button"
          disabled={
            !editor ||
            !hasInitializedEditor ||
            disabled ||
            isSubmitting ||
            !message.trim() ||
            Boolean(update && !isDirty)
          }
          onClick={handleSubmit}
        >
          {isSubmitting ? <LoadingSpinner className="h-4 w-4" /> : null}
          {update ? "Save" : "Add update"}
        </Button>
      </div>
      <ActionConfirmationModal
        open={confirmCancel}
        setOpen={setConfirmCancel}
        title="Unsaved changes"
        description={SLIDEOVER_CONFIRM_CLOSE_DESCRIPTION}
        buttonText="Confirm"
        actionVariant="default"
        afterActionFn={() => onCancel?.()}
      />
    </div>
  );
}
